import { useQueryClient } from '@tanstack/react-query'
import { Building2, Check, ChevronsUpDown, Plus } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { trpc } from '@/trpc/client'

export function OrgSwitcher() {
	const [popoverOpen, setPopoverOpen] = useState(false)
	const [createOpen, setCreateOpen] = useState(false)

	const orgsQuery = trpc.organizations.list.useQuery()
	const currentQuery = trpc.organizations.getCurrent.useQuery()
	const queryClient = useQueryClient()

	const setActive = trpc.organizations.setActive.useMutation({
		onSuccess: async () => {
			// Switch happened — every cached query is now scoped to the wrong org.
			await queryClient.invalidateQueries()
		},
		onError: (err) => {
			toast.error(err.message || 'Could not switch organizations.')
		},
	})

	const isLoading = orgsQuery.isPending || currentQuery.isPending
	const currentId = currentQuery.data?.id ?? null
	const currentName = currentQuery.data?.name ?? 'Organization'

	function handleSwitch(orgId: string) {
		setPopoverOpen(false)
		if (orgId === currentId) return
		setActive.mutate({ organizationId: orgId })
	}

	function handleOpenCreate() {
		setPopoverOpen(false)
		setCreateOpen(true)
	}

	if (isLoading) {
		return (
			<div className="px-3 pb-2">
				<Skeleton className="h-9 w-full" />
			</div>
		)
	}

	return (
		<>
			<div className="px-3 pb-2">
				<Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
					<PopoverTrigger asChild>
						<Button
							variant="outline"
							role="combobox"
							aria-expanded={popoverOpen}
							className="w-full justify-between font-normal"
							disabled={setActive.isPending}
						>
							<span className="flex min-w-0 items-center gap-2">
								<Building2 className="size-4 shrink-0 text-muted-foreground" />
								<span className="truncate">{currentName}</span>
							</span>
							<ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-60 p-1" align="start" sideOffset={6}>
						<div className="max-h-64 overflow-y-auto py-1">
							{orgsQuery.data?.map((org) => (
								<button
									key={org.id}
									type="button"
									onClick={() => handleSwitch(org.id)}
									className={cn(
										'flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm',
										'hover:bg-accent hover:text-accent-foreground',
										org.id === currentId && 'bg-accent/50',
									)}
								>
									<span className="flex min-w-0 items-center gap-2">
										<Building2 className="size-4 shrink-0 text-muted-foreground" />
										<span className="truncate">{org.name}</span>
									</span>
									{org.id === currentId ? (
										<Check className="size-4 shrink-0 text-primary" />
									) : null}
								</button>
							))}
						</div>
						<div className="my-1 border-t" />
						<button
							type="button"
							onClick={handleOpenCreate}
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
						>
							<Plus className="size-4 text-muted-foreground" />
							Create organization
						</button>
					</PopoverContent>
				</Popover>
			</div>
			<CreateOrgDialog open={createOpen} onOpenChange={setCreateOpen} />
		</>
	)
}

interface CreateOrgDialogProps {
	open: boolean
	onOpenChange: (v: boolean) => void
}

function CreateOrgDialog({ open, onOpenChange }: CreateOrgDialogProps) {
	const [name, setName] = useState('')
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const createOrg = trpc.organizations.create.useMutation()
	const setActive = trpc.organizations.setActive.useMutation()

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault()
		const trimmed = name.trim()
		if (!trimmed) return
		try {
			const created = await createOrg.mutateAsync({ name: trimmed })
			await setActive.mutateAsync({ organizationId: created.id })
			await queryClient.invalidateQueries()
			setName('')
			onOpenChange(false)
			toast.success(`Switched to ${created.name}`)
			navigate('/app')
		} catch (err) {
			const message =
				err instanceof Error ? err.message : 'Could not create organization'
			toast.error(message)
		}
	}

	const pending = createOrg.isPending || setActive.isPending

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<form onSubmit={onSubmit}>
					<DialogHeader>
						<DialogTitle>Create organization</DialogTitle>
						<DialogDescription>
							A separate workspace with its own items, categories, and members.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-2 py-4">
						<Label htmlFor="org-name">Name</Label>
						<Input
							id="org-name"
							autoFocus
							placeholder="My family, Work, etc."
							value={name}
							onChange={(e) => setName(e.target.value)}
							maxLength={80}
							disabled={pending}
							required
						/>
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="ghost"
							onClick={() => onOpenChange(false)}
							disabled={pending}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={pending || name.trim().length === 0}>
							{pending ? 'Creating…' : 'Create'}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}
